import { FC, Fragment, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { Helmet } from 'react-helmet-async';
import PageTitleWrapper from '../../../components/PageTitleWrapper';
import SitesStockSummaryHeader from './PageHeader';
import PageContainerWrapper from '../../../components/PageContainerWrapper';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Button, Link, Tooltip } from '@mui/material';
import { tableStyles } from '../Reports';
import { getSitesStockSummaryDataAction, getSitesStockSummaryResetAction } from './actions';
import { showAlertAction } from '../../../store/alerts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDisplayDateTime } from '../../../../src/utils';
import { useSearchParams } from 'react-router-dom'; // at the top


export interface SitesStockSummaryProps {}

// Group data by party name
const groupByParty = (data: any[]): Array<{partyName: string, items: any[]}> => {
  if (!data) return []
  
  const grouped = data.reduce((acc, item) => {
    const partyName = item.party
    if (partyName === 'SUM') {
      // Find the previous party and add SUM to it
      const lastPartyKey = Object.keys(acc).pop()
      if (lastPartyKey && lastPartyKey !== 'SUM') {
        acc[lastPartyKey].push(item)
      } else {
        // If no previous party, create a SUM group
        if (!acc['SUM']) {
          acc['SUM'] = []
        }
        acc['SUM'].push(item)
      }
    } else {
      if (!acc[partyName]) {
        acc[partyName] = []
      }
      acc[partyName].push(item)
    }
    return acc
  }, {} as Record<string, any[]>)
  
  return Object.entries(grouped).map(([partyName, items]) => ({
    partyName,
    items: items as any[]
  }))
}

export const SitesStockSummary: FC<SitesStockSummaryProps> = ({}) => {
  const classes = tableStyles();
  const { message, success, data: siteReportRows } = useAppSelector(
    (state) => state.sitesStockSummaryState
  );
  const dispatchAction = useAppDispatch();
  const [searchString, setSearchString] = useState('');
  const [filteredRows, setFilteredRows] = useState(siteReportRows);
  const [searchParams] = useSearchParams();
  const partyId = searchParams.get('party_id');

  useEffect(() => {
    dispatchAction(getSitesStockSummaryResetAction());
    dispatchAction(getSitesStockSummaryDataAction(partyId)); // Assuming your action supports param
  }, [dispatchAction, partyId]);

  useEffect(() => {
    if (!success && message) {
      dispatchAction(
        showAlertAction({
          message: message,
          severity: 'error',
        })
      );
    }
  }, [siteReportRows, message, success]);

  useEffect(() => {
    const normalizedSearch = searchString.trim().toLowerCase();
    const isExactMatch = searchString.endsWith('  ');

    if (normalizedSearch.length > 0) {
      const filtered = siteReportRows.filter((bill) => {
        const searchTarget = `${bill.party} ${bill.siteAddress} ${bill.siteNo} ${bill.itemName}`.toLowerCase();
        return isExactMatch
          ? ( bill.party?.toLowerCase() === normalizedSearch || bill.siteAddress?.toLowerCase() === normalizedSearch || `${bill.siteNo}`.toLowerCase() === normalizedSearch || bill.itemName?.toLowerCase() === normalizedSearch)
          : searchTarget.includes(normalizedSearch) && bill.party !== 'SUM';
      });
      setFilteredRows(filtered);
    } else {
      setFilteredRows(siteReportRows);
    }
  }, [searchString, siteReportRows]);

  const exportTableToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ['S.N.', 'Site', 'Item', 'Issued', 'Received', 'Balance'];
    const tableRows: any[] = [];
    let issued = 0;
    let received = 0;
    let balance = 0;
    let count = 0;
  
    filteredRows.forEach((bill) => {
      const isNotSum = bill.party !== 'SUM';
      issued += isNotSum ? bill.issuedQuantity : 0;
      received += isNotSum ? bill.receivedQuantity : 0;
      balance += isNotSum ? bill.balanceQuantity : 0;
  
      const rowData = [
        isNotSum ? ++count : '',
        isNotSum ? bill.siteAddress : 'Sum',
        isNotSum ? bill.itemName : '',
        bill.issuedQuantity,
        bill.receivedQuantity,
        bill.balanceQuantity,
      ];
      tableRows.push(rowData);
    });
  
    if (tableRows.length > 0) {
      const grandSumRow = [
        '',
        'Grand Sum',
        '',
        issued,
        received,
        balance,
      ];
      tableRows.push(grandSumRow);
    }

    doc.text('Balance Items '+(getDisplayDateTime(new Date())), 14, 14);
    

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'grid',
      didParseCell: (data) => {
        const isGrandSumRow = data.row.raw[1] === 'Grand Sum' || data.row.raw[1] === 'Sum';
        if (isGrandSumRow) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = '#000000'; // Optional: Change text color
        }
      },
    });
  
    doc.save('site-stock-summary.pdf');
  };
  
  

  let count = 0;
  const groupedData: Array<{partyName: string, items: any[]}> = groupByParty(filteredRows || []);
  
  return (
    <>
      <Helmet>
        <title>Billing Report</title>
      </Helmet>
      <PageTitleWrapper>
        <SitesStockSummaryHeader
          searchString={searchString}
          setSearchString={setSearchString}
          partyId={partyId}
          onPdfClick={exportTableToPDF}
        />
      </PageTitleWrapper>
      <PageContainerWrapper>
       
        <Table aria-label="billing report">
          <TableHead>
            <TableRow>
              <TableCell width={20}>S.N.</TableCell> {/* Serial Number column */}
              <TableCell className={classes.tableCell}>Site</TableCell>
              <TableCell className={classes.tableCell}>Site ID</TableCell>
              <TableCell className={classes.tableCell} sx={{width: 200}}>Item</TableCell>
              <TableCell className={classes.tableCell}>Issued</TableCell>
              <TableCell className={classes.tableCell}>Received</TableCell>
              <TableCell className={classes.tableCell}>Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {              groupedData?.map((group) => {
                return (
                  <Fragment key={group.partyName}>
                    {/* Party Header Row */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }
                      }}
                    >
                      <TableCell colSpan={7} sx={{ padding: '12px 16px' }}>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        >
                          {!partyId ? (
                            <a
                              href={`${window.location.pathname}?party_id=${group.items[0]?.partyId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1976d2',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '1rem'
                              }}
                            >
                              {group.partyName}
                            </a>
                          ) : (
                            group.partyName
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Site Rows for this Party */}
                    {group.items.map((bill, index) => {
                      const isNotSum = !(bill.party === 'SUM');
                      return (
                        <TableRow 
                          key={`${group.partyName}-${index}`}
                          sx={bill.party === 'SUM' ? { 
                            fontWeight: 'bold',
                            '& .MuiTableCell-root': {
                              borderTop: '1px solid #ccc'
                            }
                          } : {}}
                        >
                          <TableCell>{isNotSum ? ++count : ''}</TableCell>
                          {isNotSum ? (
                            <>
                              <TableCell className={classes.tableCell}>{bill.siteAddress}</TableCell>
                              <TableCell className={classes.tableCell}>
                                <Tooltip arrow title="Go to site details">
                                  <Link
                                    color={'#0000ff'}
                                    underline="always"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`/slips/view-slips/${bill.siteNo}/${bill.partyId}?tab_index=3`}
                                  >
                                    {bill.siteNo}
                                  </Link>
                                </Tooltip>
                              </TableCell>
                              <TableCell className={classes.tableCell}>{bill.itemName}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell colSpan={3} className={classes.tableCell}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Total</Typography>
                              </TableCell>
                            </>
                          )}
                          <TableCell className={classes.tableCell}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.issuedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.receivedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Typography variant={isNotSum ? 'body1' : 'h6'} sx={bill.party === 'SUM' ? { fontWeight: 'bold' } : {}}>
                              {bill.balanceQuantity}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
      </PageContainerWrapper>
    </>
  );
};

export default SitesStockSummary;
